import React, { useEffect, useId, useMemo, useState } from 'react';
import axios from 'axios';
import { Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import DisabledReason from './DisabledReason';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

// Module-level, so an omitted `excludeIds` is one array for every render instead of a new one each time.
const NO_IDS = [];
const NOTHING_FOUND = { term: '', rows: [], failed: false };
const MIN_TERM = 2;
/** How long typing must pause before a search is sent, so a fast typist asks once, not once per key. */
export const SEARCH_DEBOUNCE_MS = 250;

/**
 * Type-to-search over a list endpoint (#906). `GET {endpoint}?{searchParam}=<term>` plus `params`, from two
 * characters; the reply is an array or a `{data: []}` page. Rows already in `excludeIds` are hidden. A pick
 * calls `onPick(row)` and clears the box, so the caller owns what "selected" means.
 *
 * `excludeIds` is applied while rendering and keyed by its contents, so a caller that builds it inline never
 * refetches; the request depends only on the term, `endpoint`, `searchParam` and the contents of `params`.
 * The request waits for typing to pause (`SEARCH_DEBOUNCE_MS`); a term shorter than two characters clears the
 * list at once. `emptyText` opts in to a "nothing found" line once a search has answered with no visible row,
 * and a search that FAILED always says so ("Search failed — try again"): an error is never read as "no match".
 * `disabled` disables the box; `disabledReason` disables it too and explains why, as its title (the mouse tooltip) and as
 * its description for assistive tech (a viewer without the permission). A box disabled without a reason says nothing:
 * the caller does not know yet whether the answer is no (permissions still loading).
 */
export default function RecordSearchSelect({
    endpoint, params, searchParam = 'search', rowKey, renderRow, excludeIds = NO_IDS, onPick, onQueryChange,
    placeholder = 'Search…', emptyText, testId = 'record-search-select', disabled = false, disabledReason,
}) {
    const { getAuthHeader } = useAuth() || {};
    const reasonId = useId();
    const [query, setQuery] = useState('');
    const [found, setFound] = useState(NOTHING_FOUND);
    // The term a request is made for: `term` once typing has paused, '' while it is too short to search.
    const [settled, setSettled] = useState('');
    const term = query.trim();
    // `params` is usually an inline object: key on its contents, then read it back from the key
    const paramsKey = JSON.stringify(params || {});
    const excludeKey = excludeIds.join('\u0000');
    const hidden = useMemo(() => new Set(excludeKey ? excludeKey.split('\u0000') : []), [excludeKey]);

    useEffect(() => {
        if (term.length < MIN_TERM) { setSettled(''); return undefined; }
        const timer = setTimeout(() => setSettled(term), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [term]);

    useEffect(() => {
        if (settled.length < MIN_TERM) { setFound(prev => (prev.rows.length || prev.failed ? NOTHING_FOUND : prev)); return undefined; }
        // A newer term cancels this request's answer, so a slow reply for an older term never replaces a newer one.
        let cancelled = false;
        axios.get(`${API}${endpoint}`, { params: { ...JSON.parse(paramsKey), [searchParam]: settled }, headers: getAuthHeader?.() })
            .then(res => { if (!cancelled) setFound({ term: settled, rows: Array.isArray(res.data) ? res.data : (res.data?.data || []), failed: false }); })
            .catch(() => { if (!cancelled) setFound({ term: settled, rows: [], failed: true }); });
        return () => { cancelled = true; };
    }, [settled, endpoint, searchParam, paramsKey, getAuthHeader]);

    const searched = term.length >= MIN_TERM && found.term === term;
    const rows = term.length >= MIN_TERM ? found.rows.filter(row => !hidden.has(String(row[rowKey]))) : [];
    const showError = searched && found.failed;
    const showEmpty = Boolean(emptyText) && searched && !found.failed && rows.length === 0;

    return (
        <div className="relative space-y-1" data-testid={testId}>
            <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" />
                <input value={query} onChange={e => { setQuery(e.target.value); onQueryChange?.(e.target.value); }} placeholder={placeholder} aria-label={placeholder}
                    disabled={disabled || Boolean(disabledReason)} title={disabledReason || undefined}
                    aria-describedby={disabledReason ? reasonId : undefined} data-testid={`${testId}-input`} className="w-full pl-7 pr-2 py-2 text-sm border border-stone-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed" />
            </div>
            <DisabledReason id={reasonId} reason={disabledReason} />
            {(rows.length > 0 || showEmpty || showError) && (
                <div className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto rounded-lg border border-stone-200 bg-white shadow-lg">
                    {rows.map(row => (
                        <button type="button" key={row[rowKey]} data-testid={`${testId}-option-${row[rowKey]}`}
                            onClick={() => { onPick?.(row); setQuery(''); setFound(NOTHING_FOUND); }}
                            className="block w-full px-3 py-2 text-left text-sm hover:bg-stone-50">
                            {renderRow(row)}
                        </button>
                    ))}
                    {showError && <p className="px-3 py-2 text-sm text-red-600" role="alert" data-testid={`${testId}-error`}>Search failed — try again</p>}
                    {showEmpty && <p className="px-3 py-2 text-sm text-stone-400" data-testid={`${testId}-empty`}>{emptyText}</p>}
                </div>
            )}
        </div>
    );
}
